import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Route, Router, RouterModule } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { MENU } from '../sidebar/menu';
import { MenuItem } from '../sidebar/menu.model';

export interface BreadcrumbItem {
  /** Clé de traduction (optionnelle) */
  key?: string;
  /** Libellé affiché si la clé est absente des traductions */
  fallback: string;
  /** Lien de navigation ; absent pour un élément non cliquable */
  url?: string;
}

/** Libellés de repli (les traductions sont chargées depuis le serveur et peuvent manquer) */
const FALLBACK_LABELS: Record<string, Record<string, string>> = {
  home: { fr: 'Accueil', ar: 'الرئيسية' },
  new: { fr: 'Création', ar: 'إنشاء' },
  edit: { fr: 'Modification', ar: 'تعديل' },
  view: { fr: 'Consultation', ar: 'عرض' },
};

/** Libellés de repli des segments d'URL sans entrée de menu ni clé "tmw.<entite>.list-name" */
const SEGMENT_LABELS: Record<string, string> = {
  detail: 'Détails',
  context: 'Contextes',
  transitions: 'Transitions',
  affectation: 'Affectation',
  planification: 'Planifications',
  metrics: 'Métriques',
  logs: 'Journaux',
  'notifications-list': 'Notifications',
};

const ACTIONS = ['new', 'edit', 'view'];

/**
 * Fil d'Ariane global : calculé à partir de l'URL courante, du menu (rubrique > écran)
 * et de l'arbre de routes (sous-écrans, création / modification / consultation).
 *
 * Une route peut surcharger son libellé via `data: { breadcrumb: 'cle.de.traduction' }`
 * ou être ignorée via `data: { breadcrumb: { skip: true } }`.
 */
@Component({
  standalone: true,
  selector: 'tmw-app-breadcrumb',
  templateUrl: './app-breadcrumb.component.html',
  styleUrls: ['./app-breadcrumb.component.scss'],
  imports: [RouterModule],
})
export class AppBreadcrumbComponent implements OnInit {
  private router = inject(Router);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  items: BreadcrumbItem[] = [];

  ngOnInit(): void {
    this.build();
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.build());
    this.translate.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.build());
  }

  labelOf(item: BreadcrumbItem): string {
    if (item.key) {
      const translated = this.translate.instant(item.key);
      if (translated && translated !== item.key && translated !== `[${item.key}]`) {
        return translated;
      }
    }
    return item.fallback;
  }

  build(): void {
    const url = this.router.url.split(/[?#]/)[0];
    const items: BreadcrumbItem[] = [{ key: 'tmw.breadcrumb.home', fallback: this.fallback('home'), url: '/' }];

    // Rubrique et écran du menu correspondant à l'URL (lien le plus long qui préfixe l'URL)
    const menuPath = findMenuPath(MENU, url);
    const menuLink = menuPath.length ? menuPath[menuPath.length - 1].link : undefined;
    menuPath.forEach((menuItem) => items.push({ key: menuItem.label, fallback: menuItem.label, url: menuItem.link }));

    // Niveaux de route situés sous l'écran du menu
    let snapshot = this.router.routerState.snapshot.root;
    const segments: string[] = [];
    while (snapshot.firstChild) {
      snapshot = snapshot.firstChild;
      const levelSegments = snapshot.url.map((segment) => segment.path);
      if (!levelSegments.length) {
        continue;
      }
      segments.push(...levelSegments);
      const levelUrl = '/' + segments.join('/');
      const config = snapshot.routeConfig;
      const breadcrumbData = config?.data?.['breadcrumb'];

      if (breadcrumbData?.skip || (menuLink && (menuLink === levelUrl || menuLink.startsWith(levelUrl + '/')))) {
        continue;
      }
      const item = this.itemFor(config, breadcrumbData);
      if (item) {
        items.push({ ...item, url: isNavigable(config) ? levelUrl : undefined });
      }
    }

    this.items = items;
  }

  private itemFor(config: Route | null, breadcrumbData: any): BreadcrumbItem | undefined {
    if (typeof breadcrumbData === 'string') {
      return { key: breadcrumbData, fallback: breadcrumbData };
    }
    const staticParts = (config?.path ?? '').split('/').filter((part) => part && !part.startsWith(':'));
    if (!staticParts.length) {
      return undefined;
    }
    const segment = staticParts[staticParts.length - 1];
    if (ACTIONS.includes(segment)) {
      return { key: `tmw.breadcrumb.${segment}`, fallback: this.fallback(segment) };
    }
    return { key: `tmw.${toCamelCase(segment)}.list-name`, fallback: SEGMENT_LABELS[segment] ?? humanize(segment) };
  }

  private fallback(code: string): string {
    const labels = FALLBACK_LABELS[code];
    return labels[this.translate.currentLang] ?? labels['fr'];
  }
}

function findMenuPath(menuItems: MenuItem[], url: string, parents: MenuItem[] = []): MenuItem[] {
  let best: MenuItem[] = [];
  const linkLength = (path: MenuItem[]) => (path.length ? path[path.length - 1].link?.length ?? 0 : 0);
  for (const menuItem of menuItems) {
    const path = [...parents, menuItem];
    const link = menuItem.link;
    if (link && link !== '/' && (url === link || url.startsWith(link + '/')) && link.length > linkLength(best)) {
      best = path;
    }
    if (menuItem.subItems?.length) {
      const subPath = findMenuPath(menuItem.subItems, url, path);
      if (linkLength(subPath) > linkLength(best)) {
        best = subPath;
      }
    }
  }
  return best;
}

/** Une route est navigable si elle affiche un composant, directement ou via un enfant de chemin vide */
function isNavigable(config: Route | null): boolean {
  if (!config) {
    return false;
  }
  if (config.component || config.loadComponent || config.redirectTo !== undefined) {
    return true;
  }
  // _loadedRoutes : enfants d'une route loadChildren, renseignés par le router une fois le module chargé
  const children: Route[] | undefined = config.children ?? (config as any)._loadedRoutes;
  return !!children?.some((child) => child.path === '' && (child.component || child.loadComponent || child.redirectTo !== undefined));
}

function toCamelCase(segment: string): string {
  return segment.replace(/-([a-z])/g, (_, letter: string) => letter.toUpperCase());
}

function humanize(segment: string): string {
  const label = segment.replace(/-/g, ' ');
  return label.charAt(0).toUpperCase() + label.slice(1);
}
